import { MoreHorizontal, Edit2, PenLine } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useState } from 'react';
import { Badge } from '../../../components/ui/badge';

export type GuestStatus = 'pending' | 'approved' | 'rejected';

export const getStatusColor = (status: GuestStatus) => {
  switch (status) {
    case 'pending':
      return 'bg-blue-100 text-blue-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
export interface Guest {
  id: string;
  name?: string;
  email: string;
  avatar_url?: string;
  invitation_status: 'pending' | 'invited' | 'accepted' | 'declined';
  approval_status: 'pending' | 'approved' | 'rejected';
  rsvp_status: 'pending' | 'confirmed' | 'declined';
  attendance_status: 'pending' | 'attended' | 'no_show';
  is_on_waiting_list: boolean;
  waiting_list_position?: number;
  invited_at?: string;
  responded_at?: string;
  approved_at?: string;
  approved_by?: string;
  registered_at: string;
  updated_at?: string;
  invitation_token?: string;
  invitation_message?: string;
  invited_by?: string;
}

interface GuestRowProps {
  guest: Guest;
  onStatusChange: (guestId: string, newStatus: 'approved' | 'rejected') => void;
  onRemove: (guestId: string) => void;
}

export const GuestRow = ({ guest, onStatusChange, onRemove }: GuestRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getStatusDisplay = () => {
    switch (guest.approval_status) {
      case 'pending':
        return <span className="bg-blue-100 text-blue-600 px-8 py-2 rounded">Pending</span>;
      case 'approved':
        return <span className="bg-green-100 text-green-600 px-8 py-2 rounded">Approved</span>;
      case 'rejected':
        return <span className="bg-red-100 text-red-600 px-8 py-2 rounded">Rejected</span>;
      default:
        return <span className="bg-gray-100 text-gray-600 px-8 py-2 rounded">{guest.approval_status}</span>;
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleApprove = () => {
    onStatusChange(guest.id, 'approved');
    setIsEditing(false);
  };

  const handleDeny = () => {
    onStatusChange(guest.id, 'rejected');
    setIsEditing(false);
  };

  const getStatusBadge = (guest: Guest) => {
    if (guest.is_on_waiting_list) {
      return <Badge variant="secondary" className="bg-yellow-50 text-yellow-600 px-4 py-2 rounded-md font-medium">Waiting List</Badge>;
    }
    
    if (guest.approval_status === 'pending') {
      return <Badge variant="secondary" className="bg-blue-50 text-blue-600 px-4 py-2 rounded-md font-medium">Pending</Badge>;
    }
    
    if (guest.approval_status === 'rejected') {
      return <Badge variant="destructive" className="bg-red-50 text-red-600 px-4 py-2 rounded-md font-medium">Rejected</Badge>;
    }
    
    if (guest.rsvp_status === 'confirmed') {
      return <Badge variant="approved" className="bg-green-50 text-green-600 px-4 py-2 rounded-md font-medium">Confirmed</Badge>;
    }
    
    if (guest.rsvp_status === 'declined') {
      return <Badge variant="secondary" className="bg-red-50 text-red-600 px-4 py-2 rounded-md font-medium">Declined</Badge>;
    }
    
    if (guest.approval_status === 'approved') {
      return <Badge variant="default" className="bg-green-50 text-green-600 px-4 py-2 rounded-md font-medium">Approved</Badge>;
    }
    
    return <Badge variant="secondary" className="bg-gray-50 text-gray-600 px-4 py-2 rounded-md font-medium">Invited</Badge>;
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-3 px-2 w-12">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
            {guest.avatar_url ? (
              <img src={guest.avatar_url} alt={guest.name} className="w-8 h-8 rounded-full" />
            ) : (
              <span className="text-sm font-medium text-gray-600">
                {guest.name?.charAt(0) || guest.email.charAt(0)}
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-2 w-48">
        <div>
          <div className="font-medium text-gray-900">{guest.name || guest.email.split('@')[0]}</div>
          {guest.is_on_waiting_list && guest.waiting_list_position && (
            <div className="text-xs text-gray-500">Position #{guest.waiting_list_position}</div>
          )}
        </div>
      </td>
      <td className="py-3 px-2 w-64">
        <div className="text-sm text-gray-600">{guest.email}</div>
      </td>
      <td className="py-3 px-2 w-48">
        <div className="min-h-[40px] flex items-center">
          {isEditing && guest.approval_status === 'pending' ? (
            <div className="flex space-x-2">
              <Button
                variant="outline-approve"
                size="sm"
                onClick={handleApprove}
                className="flex items-center space-x-1 font-medium"
              >
                <span>✓</span>
                <span>Approve</span>
              </Button>
              <Button
                variant="outline-deny"
                size="sm"
                onClick={handleDeny}
                className="flex items-center space-x-1 font-medium"
              >
                <span>✕</span>
                <span>Deny</span>
              </Button>
            </div>
          ) : (
            getStatusBadge(guest)
          )}
        </div>
      </td>
      <td className="py-3 px-2 w-32">
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(false)}
                className="px-3 py-1 text-xs hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                variant="outline-deny"
                size="sm"
                onClick={() => {
                  onRemove(guest.id);
                  setIsEditing(false);
                }}
                className="px-3 py-1 text-xs"
              >
                Remove
              </Button>
            </div>
          ) : (
            <Button
              variant='primaryGhost'
              onClick={() => setIsEditing(true)}
              className="px-2"
              style={{margin: 'auto'}}
            >
              <PenLine className="w-4 h-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};
